"use client";

import { useRef, useState, type KeyboardEvent } from "react";
import { ArrowUpRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "../../../i18n/navigation";
import {
  initialPricingAudience,
  nextPricingAudience,
  pricingAudiences,
  type PricingAudience,
} from "./pricing-tabs";

const horsemanRowKeys = [
  "liveTiming",
  "timingHistory",
  "plannerBrowsing",
  "plannerExport",
  "marketplaceBrowsing",
  "marketplaceListings",
  "stableTrial",
] as const;

const stableRowKeys = [
  "creation",
  "capacity",
  "roles",
  "records",
  "linkOwner",
] as const;

type PlanKey = "horsemanFree" | "horsemanPro" | "stableFree" | "stablePaid";

type PricingColumn = {
  key: PlanKey;
  name: string;
  price: string;
  billing: string;
  action: string;
  badge: string;
  isFeatured: boolean;
};

type PricingRow = {
  key: string;
  label: string;
  values: readonly string[];
};

type PricingGroupProps = {
  heading: string;
  description: string;
  rowHeader: string;
  note: string;
  columns: readonly PricingColumn[];
  rows: readonly PricingRow[];
};

function PricingGroup({
  heading,
  description,
  rowHeader,
  note,
  columns,
  rows,
}: PricingGroupProps) {
  return (
    <article className="landing-surface overflow-hidden border border-border-subtle">
      <div className="p-6 sm:p-8">
        <h2 className="text-2xl font-semibold tracking-[-0.03em] text-primary sm:text-3xl">
          {heading}
        </h2>
        <p className="mt-3 max-w-xl text-body text-secondary">{description}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-120 table-fixed border-collapse">
          <caption className="sr-only">{heading}</caption>
          <colgroup>
            <col className="w-[40%]" />
            {columns.map((column) => (
              <col key={column.key} className="w-[30%]" />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th
                scope="col"
                className="border-y border-e border-border-subtle p-4 text-start align-bottom font-sans text-body-small font-bold uppercase tracking-[0.14em] text-secondary sm:p-6"
              >
                {rowHeader}
              </th>

              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`border-y border-border-subtle p-4 text-start align-top font-normal sm:p-6 ${
                    column.isFeatured
                      ? "pricing-column--featured-header"
                      : "border-e"
                  }`}
                >
                  <span className="flex min-h-6 items-center">
                    {column.badge ? (
                      <span className="inline-flex min-h-6 items-center bg-brand px-2 font-sans text-body-small font-bold uppercase tracking-[0.12em] text-primary">
                        {column.badge}
                      </span>
                    ) : null}
                  </span>

                  <span className="mt-3 block text-lg font-semibold tracking-[-0.02em] text-primary sm:text-xl">
                    {column.name}
                  </span>
                  <span className="mt-3 block text-2xl font-semibold tracking-[-0.03em] text-primary sm:text-3xl">
                    {column.price}
                  </span>
                  <span className="mt-2 block text-xs leading-5 text-secondary">
                    {column.billing}
                  </span>

                  <Link
                    href="/login"
                    className={`mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 border px-3 text-center text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 ${
                      column.isFeatured
                        ? "border-accent bg-accent text-accent-ink hover:bg-accent-hover"
                        : "border-border-default text-brand-ink hover:bg-brand-subtle"
                    }`}
                  >
                    {column.action}
                    <ArrowUpRight
                      className="size-4 shrink-0 rtl:-scale-x-100"
                      aria-hidden="true"
                    />
                  </Link>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th
                  scope="row"
                  className="border-b border-e border-border-subtle p-4 text-start text-sm font-medium leading-5 text-secondary sm:px-6"
                >
                  {row.label}
                </th>

                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={`border-b border-border-subtle p-4 text-start text-sm font-medium leading-5 text-primary sm:px-6 ${
                      column.isFeatured
                        ? "pricing-column--featured"
                        : "border-e"
                    }`}
                  >
                    {row.values[index]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="max-w-3xl p-6 text-sm leading-6 text-secondary sm:p-8">
        {note}
      </p>
    </article>
  );
}

export function PricingView() {
  const t = useTranslations("Home.pricing");
  const isRtl = useLocale() === "ar";
  const [audience, setAudience] = useState<PricingAudience>(
    initialPricingAudience,
  );
  const tabRefs = useRef<Partial<Record<PricingAudience, HTMLButtonElement>>>(
    {},
  );

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next = nextPricingAudience(audience, event.key, isRtl);

    if (!next) {
      return;
    }

    event.preventDefault();
    setAudience(next);
    tabRefs.current[next]?.focus();
  };

  const toColumn = (key: PlanKey, isFeatured: boolean): PricingColumn => ({
    key,
    name: t(`plans.${key}.name`),
    price: t(`plans.${key}.price`),
    billing: t(`plans.${key}.billing`),
    action: t(`plans.${key}.action`),
    badge: t(`plans.${key}.badge`),
    isFeatured,
  });

  const horsemanRows = horsemanRowKeys.map((key) => ({
    key,
    label: t(`groups.horseman.rows.${key}.label`),
    values: [
      t(`groups.horseman.rows.${key}.free`),
      t(`groups.horseman.rows.${key}.paid`),
    ],
  }));

  const stableRows = stableRowKeys.map((key) => ({
    key,
    label: t(`groups.stable.rows.${key}.label`),
    values: [
      t(`groups.stable.rows.${key}.free`),
      t(`groups.stable.rows.${key}.paid`),
    ],
  }));

  return (
    <div className="landing-canvas px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-brand-ink">
          {t("eyebrow")}
        </p>
        <h1
          id="pricing-heading"
          className="mt-3 text-balance text-4xl font-bold leading-[1.08] tracking-[-0.04em] text-primary sm:text-5xl lg:text-6xl"
        >
          {t("heading")}
        </h1>
        <p className="mx-auto mt-6 max-w-3xl text-balance text-base leading-[1.625] text-secondary">
          {t("description")}
        </p>
      </div>

      <div className="mt-10 flex justify-center">
        <div
          role="tablist"
          aria-label={t("audienceLabel")}
          className="inline-flex border border-border-default"
        >
          {pricingAudiences.map((key) => {
            const isActive = key === audience;

            return (
              <button
                key={key}
                ref={(node) => {
                  if (node) {
                    tabRefs.current[key] = node;
                  }
                }}
                type="button"
                role="tab"
                id={`pricing-tab-${key}`}
                aria-selected={isActive}
                aria-controls={`pricing-panel-${key}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setAudience(key)}
                onKeyDown={handleTabKeyDown}
                className={`inline-flex min-h-11 items-center justify-center px-6 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset ${
                  isActive
                    ? "bg-brand text-primary"
                    : "text-secondary hover:bg-brand-subtle"
                }`}
              >
                {t(`groups.${key}.eyebrow`)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto mt-8 max-w-6xl">
        <div
          role="tabpanel"
          id="pricing-panel-horseman"
          aria-labelledby="pricing-tab-horseman"
          hidden={audience !== "horseman"}
        >
          <PricingGroup
            heading={t("groups.horseman.heading")}
            description={t("groups.horseman.description")}
            rowHeader={t("groups.horseman.rowHeader")}
            note={t("groups.horseman.note")}
            columns={[
              toColumn("horsemanFree", false),
              toColumn("horsemanPro", true),
            ]}
            rows={horsemanRows}
          />
        </div>

        <div
          role="tabpanel"
          id="pricing-panel-stable"
          aria-labelledby="pricing-tab-stable"
          hidden={audience !== "stable"}
        >
          <PricingGroup
            heading={t("groups.stable.heading")}
            description={t("groups.stable.description")}
            rowHeader={t("groups.stable.rowHeader")}
            note={t("groups.stable.note")}
            columns={[
              toColumn("stableFree", false),
              toColumn("stablePaid", true),
            ]}
            rows={stableRows}
          />
        </div>
      </div>

      <p className="mx-auto mt-8 max-w-3xl text-center text-sm leading-6 text-secondary">
        {t("independenceNotice")}
      </p>
    </div>
  );
}
